import { AdminController } from './admin.controller'
import { CampaignsAdminController } from './pages/campaigns-admin/campaigns-admin.controller'
import { GeneralSettingsController } from './pages/general-settings/general-settings.controller'
import { GroupsAdminController } from './pages/groups-admin/groups-admin.controller'
import { PostCategoriesController } from './pages/post-categories/post-categories.controller'
import { TagsController } from './pages/tags/tags.controller'
import { FormatsController } from './pages/formats/formats.controller'
import { WorkflowBriefingsController } from './pages/workflow/workflow-briefings/workflow-briefings.controller'
import { BriefingDetailsController } from './pages/briefings/briefing-details.controller'
import { UsersController } from './pages/users/users.controller'
import { BackOfficeSettingsController } from './pages/backoffice-settings/backoffice-settings.controller'
import { AccountManagementController } from './pages/account-management/account-management.controller'
import { GeneralBackofficeController } from './pages/general-backoffice/general-backoffice.controller'
import { GuestsController } from './pages/guests/guests.controller'
import { IntegrationsController } from './pages/integrations/integrations.controller'
import { TaskTagsController } from './pages/task-tags/task-tags.controller'
import { ProductsController } from './pages/products/products.controller'

const checkBriefingsLimitReached = [
  '$q',
  '$state',
  'BriefingService',
  ($q, $state, BriefingService) => {
    const deferred = $q.defer()

    BriefingService.validateCompanyFeatures().then((companyFeatures) => {
      if (!companyFeatures.maxBriefings.briefingsLimitReached) {
        deferred.resolve()
      } else {
        deferred.reject()
      }
    })

    return deferred.promise
      .catch(() => {
        $state.go('workflow.briefings')
      })
  }
]

const checkModuleEnabled = [
  '$q',
  '$state',
  '$rootScope',
  ($q, $state, $rootScope) => {
    const deferred = $q.defer()

    Promise.all([
      $rootScope.getCompanyFeaturesAndAddons(),
      $rootScope.getCompanyFeatures()
    ]).then(([{ features }, companyFeatures]) => {
      if (features['SHARE.GUEST'] || companyFeatures.includes('GUEST_ASSET_PACK')) {
        deferred.resolve()
      } else {
        deferred.reject()
      }
    })

    return deferred.promise.catch(() => {
      $state.go('workflow.home')
    })
  }
]

const checkCompanyIntegrationsFeature = [
  '$q',
  '$state',
  '$rootScope',
  ($q, $state, $rootScope) => {
    const deferred = $q.defer()
    const promises = [$rootScope.getCompanyFeatures(), $rootScope.getUser()]

    $q.all(promises).then(([features, user]) => {
      if (features.includes('EXTERNAL_LOGIN_INTEGRATION') && $rootScope.isUserSysAdminAdmin(user)) {
        deferred.resolve()
      } else {
        deferred.reject()
      }
    })

    return deferred.promise
      .catch(() => {
        $state.go('home')
      })
  }
]

const checkProductsFrameFeature = [
  '$q',
  '$state',
  '$rootScope',
  'ProductsFrameService',
  ($q, $state, $rootScope, ProductsFrameService) => {
    const deferred = $q.defer()

    $rootScope.getCompanyFeatures().then((companyFeatures) => {
      const hasFeature = ProductsFrameService.hasFeature(companyFeatures)

      if (hasFeature) {
        deferred.resolve()
      } else {
        deferred.reject()
      }
    })

    return deferred.promise
      .catch(() => {
        $state.go('home')
      })
  }
]

/* @ngInject */
function routes ($stateProvider) {
  $stateProvider
    .state('admin', {
      url: '/admin',
      abstract: true,
      views: {
        '': {
          templateUrl: '/admin/admin.html?$$REVISION$$',
          controller: AdminController
        }
      }
    })
    .state('admin.users', {
      parent: 'admin',
      url: '/users',
      views: {
        'usersList@admin': {
          templateUrl: '/admin/pages/users/users.html?$$REVISION$$',
          controller: UsersController
        },
        'secondLevelMenu@admin.users': {
          templateUrl: '/admin/admin-sidebar-menu.html?$$REVISION$$'
        }
      },
      data: {
        selectedMenuId: 1
      }
    })
    .state('admin.general', {
      parent: 'admin',
      url: '/general',
      views: {
        'general@admin': {
          templateUrl: '/admin/pages/general-settings/general-settings.html?$$REVISION$$',
          controller: GeneralSettingsController
        },
        'secondLevelMenu@admin.general': {
          templateUrl: '/admin/admin-sidebar-menu.html?$$REVISION$$'
        }
      },
      data: {
        selectedMenuId: 3
      }
    })
    .state('admin.integrations', {
      parent: 'admin',
      url: '/integrations',
      views: {
        'integrations@admin': {
          templateUrl: '/admin/pages/integrations/integrations.html?$$REVISION$$',
          controller: IntegrationsController
        },
        'secondLevelMenu@admin.integrations': {
          templateUrl: '/admin/admin-sidebar-menu.html?$$REVISION$$'
        }
      },
      resolve: {
        checkCompanyIntegrationsFeature
      },
      data: {
        selectedMenuId: 17
      }
    })
    .state('admin.groups', {
      parent: 'admin',
      url: '/groups',
      views: {
        'groups@admin': {
          templateUrl: '/admin/pages/groups-admin/groups-admin.html?$$REVISION$$',
          controller: GroupsAdminController
        },
        'secondLevelMenu@admin.groups': {
          templateUrl: '/admin/admin-sidebar-menu.html?$$REVISION$$'
        }
      },
      data: {
        selectedMenuId: 4
      }
    })
    .state('admin.campaigns', {
      parent: 'admin',
      url: '/campaigns',
      views: {
        'campaigns@admin': {
          templateUrl: '/admin/pages/campaigns-admin/campaigns-admin.html?$$REVISION$$',
          controller: CampaignsAdminController
        },
        'secondLevelMenu@admin.campaigns': {
          templateUrl: '/admin/admin-sidebar-menu.html?$$REVISION$$'
        }
      },
      data: {
        selectedMenuId: 5
      }
    })
    .state('admin.postCategories', {
      parent: 'admin',
      url: '/post/categories',
      views: {
        'postCategories@admin': {
          templateUrl: '/admin/pages/post-categories/post-categories.html?$$REVISION$$',
          controller: PostCategoriesController
        },
        'secondLevelMenu@admin.postCategories': {
          templateUrl: '/admin/admin-sidebar-menu.html?$$REVISION$$'
        }
      },
      data: {
        selectedMenuId: 6
      }
    })
    .state('admin.backOffice', {
      parent: 'admin',
      url: '/backoffice',
      views: {
        'backOffice@admin': {
          templateUrl: '/admin/pages/backoffice-settings/backoffice-settings.html?$$REVISION$$',
          controller: BackOfficeSettingsController
        },
        'secondLevelMenu@admin.backOffice': {
          templateUrl: '/admin/admin-sidebar-menu.html?$$REVISION$$'
        }
      }
    })
    .state('admin.workflowBriefings', {
      parent: 'admin',
      url: '/workflow/briefings',
      views: {
        'workflowBriefings@admin': {
          templateUrl: '/admin/pages/workflow/workflow-briefings/workflow-briefings.html?$$REVISION$$',
          controller: WorkflowBriefingsController
        }
      },
      data: {
        selectedMenuId: 8,
        type: 'WORKFLOW'
      }
    })
    .state('admin.workflowBriefingNew', {
      parent: 'admin',
      url: '/workflow/briefings/new',
      resolve: {
        checkBriefingsLimitReached
      },
      views: {
        'workflowBoards@admin': {
          templateUrl: '/admin/pages/briefings/briefing-details.html?$$REVISION$$',
          controller: BriefingDetailsController
        },
        'secondLevelMenu@admin.workflowBriefingNew': {
          templateUrl: '/admin/admin-sidebar-menu.html?$$REVISION$$'
        }
      },
      params: {
        campaignId: null
      },
      data: {
        selectedMenuId: 9,
        type: 'WORKFLOW'
      }
    })
    .state('admin.workflowBriefing', {
      parent: 'admin',
      url: '/workflow/briefings/:id',
      views: {
        'workflowBoards@admin': {
          templateUrl: '/admin/pages/briefings/briefing-details.html?$$REVISION$$',
          controller: BriefingDetailsController
        },
        'secondLevelMenu@admin.workflowBriefing': {
          templateUrl: '/admin/admin-sidebar-menu.html?$$REVISION$$'
        }
      },
      data: {
        selectedMenuId: 10,
        type: 'WORKFLOW'
      }
    })
    .state('admin.auditBriefingNew', {
      parent: 'admin',
      url: '/audit/briefings/new',
      views: {
        'auditBriefings@admin': {
          templateUrl: '/admin/pages/briefings/briefing-details.html?$$REVISION$$',
          controller: BriefingDetailsController
        }
      },
      params: {
        type: null,
        campaignId: null,
        origin: null
      },
      data: {
        selectedMenuId: 12,
        type: 'AUDIT'
      }
    })
    .state('admin.auditBriefing', {
      parent: 'admin',
      url: '/audit/briefings/:id',
      views: {
        'auditBriefings@admin': {
          templateUrl: '/admin/pages/briefings/briefing-details.html?$$REVISION$$',
          controller: BriefingDetailsController
        }
      },
      params: {
        id: null,
        type: null,
        campaignId: null,
        origin: null
      },
      data: {
        selectedMenuId: 13,
        type: 'AUDIT'
      }
    })
    .state('admin.accountManagement', {
      parent: 'admin',
      url: '/account-management',
      views: {
        'accountManagement@admin': {
          templateUrl: '/admin/pages/account-management/account-management.html?$$REVISION$$',
          controller: AccountManagementController
        },
        'secondLevelMenu@admin.accountManagement': {
          templateUrl: '/admin/admin-sidebar-menu.html?$$REVISION$$'
        }
      }
    })
    .state('admin.generalBackoffice', {
      parent: 'admin',
      url: '/general-backoffice',
      views: {
        'accountManagement@admin': {
          templateUrl: '/admin/pages/general-backoffice/general-backoffice.html?$$REVISION$$',
          controller: GeneralBackofficeController
        },
        'secondLevelMenu@admin.generalBackoffice': {
          templateUrl: '/admin/admin-sidebar-menu.html?$$REVISION$$'
        }
      },
      data: {
        selectedMenuId: 14
      }
    })
    .state('admin.tags', {
      parent: 'admin',
      url: '/arts/tags',
      views: {
        'tags@admin': {
          templateUrl: '/admin/pages/tags/tags.html?$$REVISION$$',
          controller: TagsController
        },
        'secondLevelMenu@admin.tags': {
          templateUrl: '/admin/admin-sidebar-menu.html?$$REVISION$$'
        }
      },
      data: {
        selectedMenuId: 15
      }
    })
    .state('admin.formats', {
      parent: 'admin',
      url: '/arts/formats',
      views: {
        'formats@admin': {
          templateUrl: '/admin/pages/formats/formats.html?$$REVISION$$',
          controller: FormatsController
        },
        'secondLevelMenu@admin.formats': {
          templateUrl: '/admin/admin-sidebar-menu.html?$$REVISION$$'
        }
      },
      data: {
        selectedMenuId: 16
      }
    })
    .state('admin.guests', {
      parent: 'admin',
      url: '/guests',
      resolve: {
        checkModuleEnabled
      },
      views: {
        'guests@admin': {
          templateUrl: '/admin/pages/guests/guests.html?$$REVISION$$',
          controller: GuestsController
        },
        'secondLevelMenu@admin.guests': {
          templateUrl: '/admin/admin-sidebar-menu.html?$$REVISION$$'
        }
      },
      data: {
        selectedMenuId: 17
      }
    })
    .state('admin.taskTags', {
      parent: 'admin',
      url: '/requests/tags',
      views: {
        'taskTags@admin': {
          templateUrl: '/admin/pages/task-tags/task-tags.html?$$REVISION$$',
          controller: TaskTagsController
        },
        'secondLevelMenu@admin.taskTags': {
          templateUrl: '/admin/admin-sidebar-menu.html?$$REVISION$$'
        }
      },
      data: {
        selectedMenuId: 19
      }
    })
    .state('admin.products', {
      parent: 'admin',
      url: '/arts/products',
      resolve: {
        checkProductsFrameFeature
      },
      views: {
        'products@admin': {
          templateUrl: '/admin/pages/products/products.html?$$REVISION$$',
          controller: ProductsController
        },
        'secondLevelMenu@admin.products': {
          templateUrl: '/admin/admin-sidebar-menu.html?$$REVISION$$'
        }
      },
      data: {
        selectedMenuId: 15
      }
    })
}

export const adminRoutes = routes
